/**
 * Public Auction View
 * Public view showing auction results and live updates
 */
import { useState, useEffect, useCallback } from 'react';
import { axiosInstance } from '../../config/axios';
import { formatBYC, formatBYCCompact, AUCTION_STATUS_COLORS } from './useAuction';
import CareerStatsTabs from './CareerStatsTabs';

export default function PublicAuctionView({ eventId, config: initialConfig, phase, onConfigUpdate }) {
  const [config, setConfig] = useState(initialConfig);
  const [showPhotoOverlay, setShowPhotoOverlay] = useState(false);
  
  // Keep config in sync with prop updates
  useEffect(() => { if (initialConfig) setConfig(initialConfig); }, [initialConfig]);
  const [teams, setTeams] = useState([]);
  const [liveStats, setLiveStats] = useState(null);
  const [currentPlayer, setCurrentPlayer] = useState(null);
  const [recentBids, setRecentBids] = useState([]);
  const [soldPlayers, setSoldPlayers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('leaderboard');

  const fetchData = useCallback(async () => {
    try {
      // Re-fetch config to get latest auction_status/is_paused
      const [teamsRes, statsRes, configRes] = await Promise.all([
        axiosInstance.get(`/v1/events/${eventId}/auction/teams`).catch(() => ({ data: [] })),
        axiosInstance.get(`/v1/events/${eventId}/auction/live-stats`).catch(() => ({ data: null })),
        axiosInstance.get(`/v1/events/${eventId}/auction/config`).catch(() => ({ data: null })),
      ]);
      setTeams(teamsRes.data);
      setLiveStats(statsRes.data);
      
      // Update config from fresh fetch
      const latestConfig = configRes.data || config;
      if (configRes.data) {
        setConfig(configRes.data);
        if (onConfigUpdate) onConfigUpdate(configRes.data);
      }

      // Get current player if live
      const auctionLive = (latestConfig || config)?.auction_status === 'LIVE';
      if (auctionLive) {
        try {
          const playerRes = await axiosInstance.get(`/v1/events/${eventId}/auction/current-player`);
          const playerData = playerRes.data;
          if (playerData.has_current_player && playerData.player) {
            setCurrentPlayer({
              ...playerData.player,
              current_bid: playerData.current_bid,
              bid_count: playerData.bid_count,
            });
            // Fetch recent bids for current player
            try {
              const bidsRes = await axiosInstance.get(`/v1/events/${eventId}/auction/bids?player_pool_id=${playerData.player.id}&page_size=5`);
              const bidsData = Array.isArray(bidsRes.data) ? bidsRes.data : (bidsRes.data.bids || []);
              setRecentBids(bidsData);
            } catch {
              setRecentBids([]);
            }
          } else {
            setCurrentPlayer(null);
            setRecentBids([]);
          }
        } catch {
          setCurrentPlayer(null);
          setRecentBids([]);
        }
      }

      // Get sold players
      try {
        const poolRes = await axiosInstance.get(`/v1/events/${eventId}/auction/pool?status=SOLD`);
        setSoldPlayers(poolRes.data.filter(p => p.status === 'SOLD'));
      } catch {
        setSoldPlayers([]);
      }
    } catch (err) {
      console.error('Failed to fetch data:', err);
    } finally {
      setLoading(false);
    }
  }, [eventId]);

  useEffect(() => {
    fetchData();
    // Poll every 3 seconds when live, 5 seconds otherwise
    const interval = setInterval(fetchData, config?.auction_status === 'LIVE' ? 3000 : 5000);
    return () => clearInterval(interval);
  }, [fetchData, config?.auction_status]);

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#3AAFA9]"></div>
      </div>
    );
  }

  // If no config exists, show coming soon
  if (!config) {
    return (
      <div className="bg-white rounded-xl p-8 border border-gray-100 text-center" data-testid="auction-not-configured">
        <div className="w-16 h-16 mx-auto mb-4 bg-purple-100 rounded-full flex items-center justify-center">
          <i className="ph-bold ph-gavel text-2xl text-purple-600"></i>
        </div>
        <h3 className="font-bold text-lg mb-2">{phase?.title || 'Auction'}</h3>
        <p className="text-gray-500">{phase?.description || 'Auction details will be available soon.'}</p>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="public-auction-view">
      {/* Status Banner */}
      <div className="bg-white rounded-xl p-4 border border-gray-100">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <span className={`px-3 py-1.5 rounded-full text-sm font-bold ${AUCTION_STATUS_COLORS[config.auction_status]}`}>
              {config.auction_status === 'LIVE' && (
                <span className="w-2 h-2 bg-green-500 rounded-full inline-block mr-2 animate-pulse"></span>
              )}
              {config.auction_status}
            </span>
            {config.is_paused && (
              <span className="text-amber-600 text-sm font-medium">
                <i className="ph-bold ph-pause-circle mr-1"></i>Paused
              </span>
            )}
          </div>
          {liveStats && (
            <div className="flex items-center gap-4 text-sm">
              <span className="text-gray-500">
                <strong className="text-[#2B3A67]">{liveStats.players_sold || 0}</strong> Sold
              </span>
              <span className="text-gray-500">
                <strong className="text-[#2B3A67]">{liveStats.players_available || 0}</strong> Available
              </span>
            </div>
          )}
        </div>
      </div>

      {/* Live Player Card - Full Details (Read Only) */}
      {config.auction_status === 'LIVE' && currentPlayer && (
        <div className="bg-gradient-to-br from-[#2B3A67] to-[#1e2a4a] rounded-2xl text-white overflow-hidden" data-testid="live-player-card">
          {/* Header */}
          <div className="flex items-center justify-between px-6 py-3 bg-black/20">
            <span className="px-3 py-1 bg-green-500 rounded-full text-xs font-bold animate-pulse">
              NOW BIDDING
            </span>
            <span className="text-sm opacity-70 font-mono">#{currentPlayer.pool_order}</span>
          </div>
          
          {/* Player Info Section */}
          <div className="p-6 relative">
            {/* Premium SOLD Stamp */}
            {currentPlayer.status === 'SOLD' && (
              <div className="absolute top-4 right-4 z-10 transform rotate-6">
                <div className="relative">
                  <div className="absolute inset-0 bg-emerald-500/30 blur-md rounded-lg"></div>
                  <div className="relative border-2 border-emerald-400 rounded-lg px-4 py-2 bg-gradient-to-br from-emerald-600 to-emerald-800 shadow-lg">
                    <div className="flex items-center gap-2">
                      <i className="ph-fill ph-seal-check text-emerald-200 text-xl"></i>
                      <span className="text-emerald-100 text-lg font-black tracking-wide">SOLD</span>
                    </div>
                    <p className="text-emerald-200 text-xs font-bold text-center mt-0.5 truncate max-w-[120px]">
                      {currentPlayer.sold_to_team_name || 'Team'}
                    </p>
                    {currentPlayer.sold_price_byc > 0 && (
                      <p className="text-emerald-300 text-[10px] text-center font-mono">
                        ₿ {formatBYC(currentPlayer.sold_price_byc)}
                      </p>
                    )}
                  </div>
                </div>
              </div>
            )}
            
            {/* Premium UNSOLD Stamp */}
            {currentPlayer.status === 'UNSOLD' && (
              <div className="absolute top-4 right-4 z-10 transform -rotate-6">
                <div className="relative">
                  <div className="absolute inset-0 bg-rose-500/30 blur-md rounded-lg"></div>
                  <div className="relative border-2 border-rose-400 rounded-lg px-4 py-2 bg-gradient-to-br from-rose-600 to-rose-800 shadow-lg">
                    <div className="flex items-center gap-2">
                      <i className="ph-fill ph-x-circle text-rose-200 text-xl"></i>
                      <span className="text-rose-100 text-lg font-black tracking-wide">UNSOLD</span>
                    </div>
                  </div>
                </div>
              </div>
            )}
            
            <div className="flex gap-5">
              {/* Photo */}
              <div 
                className={`w-28 h-28 rounded-xl bg-white/10 flex-shrink-0 flex items-center justify-center overflow-hidden ${currentPlayer.player_photo_url ? 'cursor-pointer hover:ring-2 hover:ring-white/50 transition-all' : ''}`}
                onClick={() => currentPlayer.player_photo_url && setShowPhotoOverlay(true)}
              >
                {currentPlayer.player_photo_url ? (
                  <img src={currentPlayer.player_photo_url} alt={currentPlayer.player_name} className="w-full h-full object-cover" />
                ) : (
                  <span className="text-4xl font-extrabold">{currentPlayer.player_name?.[0] || '?'}</span>
                )}
              </div>
              
              {/* Name & Basic Info */}
              <div className="flex-1 min-w-0">
                <div className="flex items-start justify-between gap-2">
                  <div>
                    <h2 className="text-2xl font-extrabold truncate">{currentPlayer.player_name || 'Unknown Player'}</h2>
                  </div>
                </div>
                
                {/* Tags */}
                <div className="flex flex-wrap gap-2 mt-3">
                  {currentPlayer.player_age && (
                    <span className="px-2 py-1 bg-white/20 rounded text-xs font-bold">Age: {currentPlayer.player_age}</span>
                  )}
                  {currentPlayer.is_icon && (
                    <span className="px-2 py-1 bg-purple-500 rounded text-xs font-bold">ICON</span>
                  )}
                  {currentPlayer.player_category && (
                    <span className="px-2 py-1 bg-white/20 rounded text-xs font-bold">{currentPlayer.player_category}</span>
                  )}
                  {currentPlayer.player_category_level && (
                    <span className="px-2 py-1 bg-amber-500/30 rounded text-xs font-bold">Category {currentPlayer.player_category_level}</span>
                  )}
                  {currentPlayer.batting_style && (
                    <span className="px-2 py-1 bg-blue-500/30 rounded text-xs font-bold">Bat: {currentPlayer.batting_style}</span>
                  )}
                  {currentPlayer.bowling_style && (
                    <span className="px-2 py-1 bg-sky-500/30 rounded text-xs font-bold">Bowl: {currentPlayer.bowling_style}</span>
                  )}
                  {currentPlayer.bowling_type && (
                    <span className="px-2 py-1 bg-orange-500/30 rounded text-xs font-bold">{currentPlayer.bowling_type}</span>
                  )}
                </div>
              </div>
            </div>
            
            {/* Statistics - Tabbed View */}
            {currentPlayer.stats && (
              <CareerStatsTabs stats={currentPlayer.stats} playerCategory={currentPlayer.player_category} />
            )}
          </div>
          
          {/* Pricing Section */}
          <div className="bg-black/20 p-4">
            <div className="grid grid-cols-2 gap-4">
              <div className="bg-white/10 rounded-xl p-4">
                <p className="text-xs text-white/60 mb-1">Base Price</p>
                <p className="text-2xl font-extrabold text-yellow-400">₿ {formatBYC(currentPlayer.base_price_byc)}</p>
              </div>
              <div className="bg-white/10 rounded-xl p-4">
                <p className="text-xs text-white/60 mb-1">Current Bid</p>
                <p className="text-2xl font-extrabold text-green-400">
                  {currentPlayer.current_bid 
                    ? `₿ ${formatBYC(currentPlayer.current_bid.bid_amount_byc)}`
                    : recentBids[0] 
                      ? `₿ ${formatBYC(recentBids[0].bid_amount_byc)}`
                      : '-'
                  }
                </p>
                {(currentPlayer.current_bid || recentBids[0]) && (
                  <p className="text-xs text-white/50 mt-1">
                    by {currentPlayer.current_bid?.team_name || recentBids[0]?.team_name}
                  </p>
                )}
              </div>
            </div>
          </div>
          
          {/* Recent Bids (Read Only) */}
          {recentBids.length > 0 && (
            <div className="bg-black/30 p-4">
              <p className="text-xs text-white/50 mb-2 font-bold uppercase">Recent Bids</p>
              <div className="space-y-2">
                {recentBids.slice(0, 3).map((bid, idx) => (
                  <div key={bid.id || idx} className="flex items-center justify-between text-sm bg-white/5 rounded-lg px-3 py-2">
                    <span className="text-white/80">{bid.team_name}</span>
                    <span className="font-bold text-green-400">₿ {formatBYC(bid.bid_amount_byc)}</span>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      )}


      {/* Photo Overlay */}
      {showPhotoOverlay && currentPlayer?.player_photo_url && (
        <div className="fixed inset-0 bg-black/80 z-[1100] flex items-center justify-center p-4" onClick={() => setShowPhotoOverlay(false)}>
          <div className="relative max-w-2xl max-h-[80vh]">
            <button onClick={() => setShowPhotoOverlay(false)} className="absolute -top-10 right-0 text-white hover:text-gray-300">
              <i className="ph-bold ph-x text-2xl"></i>
            </button>
            <img src={currentPlayer.player_photo_url} alt={currentPlayer.player_name} className="max-w-full max-h-[80vh] rounded-xl shadow-2xl object-contain" onClick={e => e.stopPropagation()} />
            <p className="text-white text-center mt-3 font-bold">{currentPlayer.player_name}</p>
          </div>
        </div>
      )}

      {/* Tab Navigation */}
      <div className="flex gap-2 overflow-x-auto pb-2">
        {['leaderboard', 'sold', 'squads'].map(tab => (
          <button
            key={tab}
            onClick={() => setActiveTab(tab)}
            className={`px-4 py-2 rounded-lg text-sm font-bold whitespace-nowrap transition-colors ${
              activeTab === tab
                ? 'bg-[#2B3A67] text-white'
                : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
            }`}
          >
            {tab === 'leaderboard' && <><i className="ph-bold ph-trophy mr-2"></i>Leaderboard</>}
            {tab === 'sold' && <><i className="ph-bold ph-users mr-2"></i>Sold Players</>}
            {tab === 'squads' && <><i className="ph-bold ph-list-bullets mr-2"></i>Team Squads</>}
          </button>
        ))}
      </div>

      {/* Tab Content */}
      {activeTab === 'leaderboard' && (
        <LeaderboardTab teams={teams} liveStats={liveStats} eventId={eventId} />
      )}
      {activeTab === 'sold' && (
        <SoldPlayersTab players={soldPlayers} />
      )}
      {activeTab === 'squads' && (
        <TeamSquadsTab teams={teams} eventId={eventId} />
      )}

      {/* Phase Rules/Terms */}
      {(phase?.rules || phase?.terms) && (
        <div className="bg-white rounded-xl p-5 border border-gray-100">
          {phase.rules && (
            <div className="mb-4">
              <h3 className="font-bold mb-2 flex items-center gap-2">
                <i className="ph-bold ph-list-checks text-[#3AAFA9]"></i>
                Auction Rules
              </h3>
              <p className="text-sm text-gray-600 whitespace-pre-wrap">{phase.rules}</p>
            </div>
          )}
          {phase.terms && (
            <div>
              <h3 className="font-bold mb-2 flex items-center gap-2">
                <i className="ph-bold ph-scroll text-[#E54562]"></i>
                Terms & Conditions
              </h3>
              <p className="text-sm text-gray-600 whitespace-pre-wrap">{phase.terms}</p>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

// Leaderboard Tab (Team Wallets + Expandable Squads)
function LeaderboardTab({ teams, liveStats, eventId }) {
  const sortedTeams = [...teams].sort((a, b) => {
    const spentA = (a.wallet_total_spent_byc || 0);
    const spentB = (b.wallet_total_spent_byc || 0);
    return spentB - spentA;
  });
  const [expandedTeam, setExpandedTeam] = useState(null);
  const [squads, setSquads] = useState({});

  // Clear squad cache when team data changes (player sold/unsold)
  const squadKey = teams.map(t => `${t.id}:${t.squad_count}`).join(',');
  useEffect(() => { setSquads({}); }, [squadKey]);

  const fetchSquad = async (teamId) => {
    if (squads[teamId]) return;
    try {
      const res = await axiosInstance.get(`/v1/events/${eventId}/auction/teams/${teamId}/squad`);
      setSquads(prev => ({ ...prev, [teamId]: res.data }));
    } catch (err) {
      console.error('Failed to fetch squad:', err);
    }
  };

  const handleToggle = (teamId) => {
    if (expandedTeam === teamId) {
      setExpandedTeam(null);
    } else {
      setExpandedTeam(teamId);
      fetchSquad(teamId);
    }
  };

  return (
    <div className="bg-white rounded-xl border border-gray-100 overflow-hidden">
      {/* Summary Stats */}
      {liveStats && (
        <div className="grid grid-cols-3 gap-2 sm:gap-4 p-3 sm:p-4 bg-gray-50 border-b border-gray-100">
          <div className="text-center">
            <p className="text-lg sm:text-2xl font-extrabold text-[#2B3A67]">₿ {formatBYCCompact(liveStats.total_byc_spent || 0)}</p>
            <p className="text-[10px] sm:text-xs text-gray-500">Total Spent</p>
          </div>
          <div className="text-center">
            <p className="text-lg sm:text-2xl font-extrabold text-[#3AAFA9]">₿ {formatBYCCompact(liveStats.average_sale_price || 0)}</p>
            <p className="text-[10px] sm:text-xs text-gray-500">Avg. Price</p>
          </div>
          <div className="text-center">
            <p className="text-lg sm:text-2xl font-extrabold text-[#E54562]">₿ {formatBYCCompact(liveStats.highest_sale_price || 0)}</p>
            <p className="text-[10px] sm:text-xs text-gray-500">Highest Sale</p>
          </div>
        </div>
      )}

      {/* Team Rankings with Expandable Squads */}
      <div className="divide-y divide-gray-50">
        {sortedTeams.map((team, idx) => (
          <div key={team.id}>
            <button
              onClick={() => handleToggle(team.id)}
              className="w-full flex items-center justify-between p-3 sm:p-4 hover:bg-gray-50 transition-colors"
              data-testid={`team-squad-toggle-${team.id}`}
            >
              <div className="flex items-center gap-2 sm:gap-4 min-w-0">
                <div className={`w-6 h-6 sm:w-8 sm:h-8 rounded-full flex items-center justify-center font-bold text-xs sm:text-sm flex-shrink-0 ${
                  idx === 0 ? 'bg-yellow-400 text-yellow-900' :
                  idx === 1 ? 'bg-gray-300 text-gray-700' :
                  idx === 2 ? 'bg-amber-600 text-white' :
                  'bg-gray-100 text-gray-600'
                }`}>
                  {idx + 1}
                </div>
                <div className="flex items-center gap-2 sm:gap-3 min-w-0">
                  <div className="w-8 h-8 sm:w-10 sm:h-10 rounded-lg bg-[#2B3A67] flex items-center justify-center overflow-hidden flex-shrink-0">
                    {team.team_logo_url ? (
                      <img src={team.team_logo_url} alt={team.team_name} className="w-full h-full object-cover" />
                    ) : (
                      <span className="text-white font-bold text-xs sm:text-base">{team.team_name[0]}</span>
                    )}
                  </div>
                  <div className="text-left min-w-0">
                    <p className="font-bold text-sm sm:text-base truncate">{team.team_name}</p>
                    <p className="text-[10px] sm:text-xs text-gray-500">{team.squad_count || 0} players</p>
                  </div>
                </div>
              </div>
              <div className="flex items-center gap-4">
                <div className="text-right">
                  <p className="font-bold text-[#3AAFA9] text-sm sm:text-base">₿ <span className="sm:hidden">{formatBYCCompact(team.wallet_balance_byc || 0)}</span><span className="hidden sm:inline">{formatBYC(team.wallet_balance_byc || 0)}</span></p>
                  <p className="text-[10px] sm:text-xs text-gray-400">Bal</p>
                </div>
                <div className="text-right">
                  <p className="font-bold text-[#E54562] text-sm sm:text-base">₿ <span className="sm:hidden">{formatBYCCompact(team.wallet_total_spent_byc || 0)}</span><span className="hidden sm:inline">{formatBYC(team.wallet_total_spent_byc || 0)}</span></p>
                  <p className="text-[10px] sm:text-xs text-gray-400">Spent</p>
                </div>
                <i className={`ph-bold ph-caret-${expandedTeam === team.id ? 'up' : 'down'} text-gray-400`}></i>
              </div>
            </button>

            {/* Expanded Squad */}
            {expandedTeam === team.id && (
              <div className="border-t border-gray-100 bg-gray-50 px-4 py-3">
                {!squads[team.id] ? (
                  <div className="flex justify-center py-3">
                    <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-[#3AAFA9]"></div>
                  </div>
                ) : squads[team.id].length === 0 ? (
                  <p className="text-center text-gray-400 text-sm py-3">No players acquired yet</p>
                ) : (
                  <div className="space-y-1.5">
                    {squads[team.id].map((player, pIdx) => (
                      <div key={player.id} className="flex items-center justify-between bg-white rounded-lg px-3 py-2 border border-gray-100">
                        <div className="flex items-center gap-2.5">
                          <span className="text-[10px] text-gray-400 w-4 text-center">{pIdx + 1}</span>
                          <div className="relative">
                            {player.player_photo_url ? (
                              <img src={player.player_photo_url} alt="" className="w-8 h-8 rounded-full object-cover" />
                            ) : (
                              <span className="w-8 h-8 rounded-full bg-[#2B3A67] flex items-center justify-center text-white text-xs font-bold">
                                {player.player_name?.[0]}
                              </span>
                            )}
                            {player.acquisition_type === 'ICON' && (
                              <span className="absolute -top-1 -right-1 bg-yellow-400 text-yellow-900 text-[7px] font-bold px-0.5 rounded">IC</span>
                            )}
                            {player.acquisition_type === 'OWNER_PLAYER' && (
                              <span className="absolute -top-1 -right-1 bg-purple-500 text-white text-[7px] font-bold px-0.5 rounded">OW</span>
                            )}
                          </div>
                          <div>
                            <p className="text-sm font-semibold">{player.player_name}</p>
                            <div className="flex gap-1">
                              {player.player_category && (
                                <span className="bg-blue-50 text-blue-600 text-[9px] px-1 py-0.5 rounded font-medium">{player.player_category}</span>
                              )}
                            </div>
                          </div>
                        </div>
                        <span className="text-sm font-bold text-[#E54562]">₿ {formatBYC(player.sold_price_byc || 0)}</span>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}
          </div>
        ))}
      </div>
    </div>
  );
}

// Sold Players Tab
function SoldPlayersTab({ players }) {
  const sortedPlayers = [...players].sort((a, b) => (b.sold_price_byc || 0) - (a.sold_price_byc || 0));

  if (sortedPlayers.length === 0) {
    return (
      <div className="bg-white rounded-xl p-8 border border-gray-100 text-center">
        <p className="text-gray-400">No players sold yet</p>
      </div>
    );
  }

  return (
    <div className="bg-white rounded-xl border border-gray-100 overflow-hidden">
      <div className="divide-y divide-gray-50">
        {sortedPlayers.map((player, idx) => (
          <div key={player.id} className="flex items-center justify-between p-4 hover:bg-gray-50">
            <div className="flex items-center gap-4">
              <span className="w-6 text-center text-sm text-gray-400">{idx + 1}</span>
              <div className="w-10 h-10 rounded-full bg-[#2B3A67] flex items-center justify-center">
                <span className="text-white font-bold">{player.player_name?.[0]}</span>
              </div>
              <div>
                <p className="font-medium">{player.player_name}</p>
                <p className="text-xs text-gray-500">{player.sold_to_team_name}</p>
              </div>
            </div>
            <div className="text-right">
              <p className="font-bold">₿ {formatBYC(player.sold_price_byc)}</p>
              {player.is_icon && (
                <span className="text-[10px] bg-purple-100 text-purple-600 px-1.5 py-0.5 rounded font-bold">ICON</span>
              )}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

// Team Squads Tab
function TeamSquadsTab({ teams, eventId }) {
  const [expandedTeam, setExpandedTeam] = useState(null);
  const [squads, setSquads] = useState({});

  const fetchSquad = async (teamId) => {
    if (squads[teamId]) return;
    try {
      const res = await axiosInstance.get(`/v1/events/${eventId}/auction/teams/${teamId}/squad`);
      setSquads(prev => ({ ...prev, [teamId]: res.data }));
    } catch (err) {
      console.error('Failed to fetch squad:', err);
    }
  };

  const handleToggle = (teamId) => {
    if (expandedTeam === teamId) {
      setExpandedTeam(null);
    } else {
      setExpandedTeam(teamId);
      fetchSquad(teamId);
    }
  };

  return (
    <div className="space-y-3">
      {teams.map(team => (
        <div key={team.id} className="bg-white rounded-xl border border-gray-100 overflow-hidden">
          <button
            onClick={() => handleToggle(team.id)}
            className="w-full flex items-center justify-between p-4 hover:bg-gray-50"
          >
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-[#2B3A67] flex items-center justify-center overflow-hidden">
                {team.team_logo_url ? (
                  <img src={team.team_logo_url} alt={team.team_name} className="w-full h-full object-cover" />
                ) : (
                  <span className="text-white font-bold">{team.team_name[0]}</span>
                )}
              </div>
              <div className="text-left">
                <p className="font-bold">{team.team_name}</p>
                <p className="text-xs text-gray-500">{team.squad_count || 0} players</p>
              </div>
            </div>
            <i className={`ph-bold ph-caret-${expandedTeam === team.id ? 'up' : 'down'} text-gray-400`}></i>
          </button>
          
          {expandedTeam === team.id && (
            <div className="border-t border-gray-100 p-4 bg-gray-50">
              {!squads[team.id] ? (
                <div className="text-center py-4">
                  <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-[#3AAFA9] mx-auto"></div>
                </div>
              ) : squads[team.id].length === 0 ? (
                <p className="text-center text-gray-400 text-sm py-4">No players acquired</p>
              ) : (
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                  {squads[team.id].map(player => (
                    <div key={player.id} className="flex items-center justify-between bg-white rounded-lg p-3 border border-gray-100">
                      <div className="flex items-center gap-3">
                        <div className="relative">
                          {player.player_photo_url ? (
                            <img 
                              src={player.player_photo_url} 
                              alt={player.player_name} 
                              className="w-10 h-10 rounded-full object-cover"
                            />
                          ) : (
                            <span className="w-10 h-10 rounded-full bg-[#2B3A67] flex items-center justify-center text-white text-sm font-bold">
                              {player.player_name?.[0]}
                            </span>
                          )}
                          {player.acquisition_type === 'ICON' && (
                            <span className="absolute -top-1 -right-1 bg-yellow-400 text-yellow-900 text-[8px] font-bold px-1 rounded">
                              ICON
                            </span>
                          )}
                          {player.acquisition_type === 'OWNER_PLAYER' && (
                            <span className="absolute -top-1 -right-1 bg-purple-500 text-white text-[8px] font-bold px-1 rounded">
                              OWNER
                            </span>
                          )}
                        </div>
                        <div>
                          <p className="text-sm font-semibold truncate max-w-[140px]">{player.player_name}</p>
                          {/* Skills Capsules */}
                          <div className="flex flex-wrap gap-1 mt-1">
                            {player.player_category && (
                              <span className="bg-blue-100 text-blue-700 text-[10px] px-1.5 py-0.5 rounded-full font-medium">
                                {player.player_category}
                              </span>
                            )}
                            {player.batting_style && (
                              <span className="bg-green-100 text-green-700 text-[10px] px-1.5 py-0.5 rounded-full font-medium">
                                {player.batting_style}
                              </span>
                            )}
                            {player.bowling_type && (
                              <span className="bg-orange-100 text-orange-700 text-[10px] px-1.5 py-0.5 rounded-full font-medium">
                                {player.bowling_type}
                              </span>
                            )}
                          </div>
                        </div>
                      </div>
                      <div className="text-right">
                        <span className="text-sm font-bold text-[#E54562]">₿ {formatBYC(player.sold_price_byc || 0)}</span>
                        {player.acquisition_type !== 'AUCTION' && (
                          <p className="text-[10px] text-gray-400">
                            {player.acquisition_type === 'ICON' ? 'Icon Fee' : 'Owner Fee'}
                          </p>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}
        </div>
      ))}
    </div>
  );
}
